import type { ValidationIssue } from '../../../errors'
import type { ZodIssue, ZodSchema } from '../types'

// The zod-facing half of validation: run a schema over a value and flatten its issues. Deciding WHICH
// schema and WHICH error a failure becomes is the caller's job (see ../params.ts) — this module only knows
// how to ask zod and how to translate what it answers.
//
// This is the WRAPPER path: the runtime-owned `payload` / `invocation` / `callbackObject` a helper route or
// SYSTEM seam is called with, rather than a field a person filled in. A failure here means the CALLER is
// malformed, so there is no author label to restate against — but zod's own wording ("Invalid input:
// expected object, received string") describes the parser rather than the mistake, so the shape of the
// wrapper itself is restated against the param's name. Everything deeper keeps zod's message: it is a
// runtime contract detail, and inventing wording for it would only obscure what actually arrived.

// A `fail` callback owns the error identity; it never returns.
export type FailFn = (issues: ValidationIssue[]) => never

// Matches ./messages.ts, so the two paths word the same expectation the same way.
const EXPECTED: Record<string, string> = {
  string : 'a string',
  number : 'a number',
  boolean: 'true or false',
  object : 'an object',
  array  : 'an array',
}

export function toIssues(error: { issues?: ZodIssue[] } | undefined, param?: string): ValidationIssue[] {
  return (error?.issues ?? []).map(issue => {
    const path = (issue.path ?? []).map(String).join('.')
    const expected = (issue as { expected?: string }).expected

    // Only the wrapper's own type is restated — a deeper path is left as zod put it.
    const restated = param && !path && issue.code === 'invalid_type' && expected
      ? `«${ param }» must be ${ EXPECTED[expected] ?? expected }`
      : undefined

    return { path, message: restated ?? issue.message ?? 'invalid value', code: issue.code }
  })
}

// Parse `value`, handing the flattened issues to `fail` (which throws). Schema-less slots pass through —
// authors may declare no params/criteria at all.
export function parseSchema<T>(
  schema: ZodSchema | undefined,
  value: unknown,
  fail: FailFn,
  param?: string
): T {
  if (typeof schema?.safeParse !== 'function') {
    return value as T
  }

  const result = schema.safeParse(value)

  if (!result.success) {
    fail(toIssues(result.error, param))
  }

  return result.data as T
}
